import React, { useState } from 'react';
import { auth, db, appId } from '../utils/firebase';
import {
    GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword,
    sendPasswordResetEmail, signOut
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Icons } from '../components/Icons';
import { CREW_ROLES } from '../utils/crew';

// The plain sign-in page for crew: artists, musicians, writers and helpers.
// (Staff use the triangle on the front page.) Sign in with Google or email; new people ask to join and wait for approval.
const input = 'w-full bg-[#111] border border-gray-800 rounded p-3 text-white text-sm outline-none focus:border-green-600';

const CrewLogin = ({ onClose, onDone }) => {
    const [mode, setMode] = useState('login'); // login | join
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [handle, setHandle] = useState('');
    const [role, setRole] = useState('ARTIST');
    const [note, setNote] = useState('');
    const [needsProfile, setNeedsProfile] = useState(false); // a new Google account that still has to say who they are
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');

    const profileRef = (uid) => doc(db, 'artifacts', appId, 'public', 'data', 'users', uid);

    // After any sign-in: decide what happens next.
    const afterSignIn = async (user, haveForm) => {
        const snap = await getDoc(profileRef(user.uid)).catch(() => null);
        if (snap && snap.exists()) {
            if (snap.data().status === 'PENDING') {
                await signOut(auth);
                setInfo('You are on the list. We approve new crew by hand, so check back soon.');
                return 'pending';
            }
            return 'ok'; // approved: the app takes it from here
        }
        // no profile yet: a brand-new account
        if (!haveForm) { setNeedsProfile(true); return 'needs-profile'; }
        await setDoc(profileRef(user.uid), {
            handle: handle.trim(), email: user.email, status: 'PENDING', clearance: 'LEVEL 1',
            requestedRole: role, ...(note.trim() ? { note: note.trim().slice(0, 500) } : {}), timestamp: serverTimestamp()
        });
        await signOut(auth);
        setNeedsProfile(false);
        setInfo('Request sent! We will approve you soon and then you can sign in.');
        return 'requested';
    };

    const run = async (fn) => {
        setBusy(true); setError(''); setInfo('');
        window.__crewAuthBusy = true;
        let result = null;
        try { result = await fn(); }
        catch (e) {
            const code = (e && e.code) || '';
            if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') { /* they just closed it */ }
            else if (code === 'auth/email-already-in-use') setError('That email already has an account. Use Sign in instead.');
            else if (code === 'auth/weak-password') setError('Pick a longer password (6 or more characters).');
            else if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') setError('Email or password is wrong.');
            else if (code === 'auth/unauthorized-domain') setError('This web address is not approved for sign-in yet.');
            else if (code === 'permission-denied' || /permission/i.test((e && e.message) || '')) setError('The database said no. Tell the team and we will fix it.');
            else setError('Something went wrong. Try again.');
        }
        window.__crewAuthBusy = false;
        setBusy(false);
        if (result === 'ok' && onDone) onDone();
    };

    const needsJoinFields = () => {
        if (!handle.trim()) { setError('Pick a handle (the name we will call you).'); return true; }
        return false;
    };

    const google = () => run(async () => {
        if (mode === 'join' && needsJoinFields()) return null;
        const cred = await signInWithPopup(auth, new GoogleAuthProvider());
        return afterSignIn(cred.user, mode === 'join' && !!handle.trim());
    });

    const finishProfile = () => run(async () => {
        if (needsJoinFields()) return null;
        return afterSignIn(auth.currentUser, true);
    });

    const submitEmail = (e) => {
        e.preventDefault();
        run(async () => {
            if (mode === 'join') {
                if (needsJoinFields()) return null;
                const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
                return afterSignIn(cred.user, true);
            }
            const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
            return afterSignIn(cred.user, false);
        });
    };

    const reset = async () => {
        const to = email.trim() || window.prompt('Email for the password reset link:');
        if (!to) return;
        try { await sendPasswordResetEmail(auth, to); setInfo(`Reset link sent to ${to}.`); setError(''); }
        catch (e) { setError('Could not send that reset link.'); }
    };

    return (
        <div className="fixed inset-0 bg-black z-[100] overflow-y-auto font-sans text-white" data-testid="crew-login">
            <div className="min-h-full flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-[#0a0a0a] border border-gray-800 p-8 md:p-10 shadow-2xl relative rounded">
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-white" aria-label="Close"><Icons.X /></button>
                    <div className="text-center mb-8">
                        <div className="h-12 w-12 mx-auto text-gray-500 mb-4"><Icons.TriangleLogo className="w-full h-full" /></div>
                        <h2 className="text-xl font-black tracking-[0.25em] uppercase">CREW LOGIN</h2>
                        <p className="text-[11px] text-gray-500 mt-2 tracking-widest uppercase">Artists &middot; Musicians &middot; Writers &middot; Helpers</p>
                    </div>

                    {needsProfile ? (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-300 text-center">One more step: tell us who you are.</p>
                            <input value={handle} onChange={e => setHandle(e.target.value)} maxLength={40} placeholder="Your handle (what we call you)" className={input} />
                            <select value={role} onChange={e => setRole(e.target.value)} className={input}>
                                {Object.entries(CREW_ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}: {v.blurb}</option>)}
                            </select>
                            <textarea value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="A link to your work, or a line about you (optional)" className={`${input} h-20`} />
                            <button onClick={finishProfile} disabled={busy} className="w-full bg-green-700 hover:bg-green-600 text-white py-3 font-bold uppercase tracking-widest text-xs rounded disabled:opacity-50">Ask to join</button>
                        </div>
                    ) : (
                        <>
                            <div className="flex mb-6 border border-gray-800 rounded overflow-hidden text-xs font-bold uppercase tracking-widest">
                                <button onClick={() => { setMode('login'); setError(''); setInfo(''); }} className={`flex-1 py-3 ${mode === 'login' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>Sign in</button>
                                <button onClick={() => { setMode('join'); setError(''); setInfo(''); }} className={`flex-1 py-3 ${mode === 'join' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>Join the crew</button>
                            </div>

                            {mode === 'join' && (
                                <div className="space-y-3 mb-4">
                                    <input value={handle} onChange={e => setHandle(e.target.value)} maxLength={40} placeholder="Your handle (what we call you)" className={input} />
                                    <select value={role} onChange={e => setRole(e.target.value)} className={input}>
                                        {Object.entries(CREW_ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}: {v.blurb}</option>)}
                                    </select>
                                    <textarea value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="A link to your work, or a line about you (optional)" className={`${input} h-16`} />
                                </div>
                            )}

                            <button onClick={google} disabled={busy} className="w-full py-3 bg-white text-black font-bold text-sm rounded flex items-center justify-center gap-2 disabled:opacity-50">
                                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.7a14.5 14.5 0 0 1 0-9.4l-7.9-6.1a24 24 0 0 0 0 21.6l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                                Continue with Google
                            </button>
                            <div className="flex items-center gap-3 my-4 text-[10px] text-gray-600"><span className="flex-1 h-px bg-gray-800" />OR USE EMAIL<span className="flex-1 h-px bg-gray-800" /></div>

                            <form onSubmit={submitEmail} className="space-y-3">
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" autoComplete="email" className={input} required />
                                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" autoComplete={mode === 'join' ? 'new-password' : 'current-password'} className={input} required />
                                <button type="submit" disabled={busy} className="w-full bg-green-700 hover:bg-green-600 text-white py-3 font-bold uppercase tracking-widest text-xs rounded disabled:opacity-50">{busy ? 'One moment...' : (mode === 'join' ? 'Ask to join' : 'Sign in')}</button>
                            </form>
                            {mode === 'login' && <button onClick={reset} className="mt-4 text-[11px] text-gray-500 hover:text-white underline underline-offset-4 block mx-auto">Forgot your password?</button>}
                            {mode === 'join' && <p className="mt-4 text-[11px] text-gray-500 text-center">We approve every new crew member by hand.</p>}
                        </>
                    )}

                    {error && <p className="mt-4 text-sm text-red-400 text-center" role="alert">{error}</p>}
                    {info && <p className="mt-4 text-sm text-green-400 text-center" role="status">{info}</p>}
                </div>
            </div>
        </div>
    );
};

export default CrewLogin;
